import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stepper, Card, Input, Button, Alert, Badge, Loader } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import { formatINR, errorText } from '@/lib/format';
import { PageHeader } from '@/components';
import SellPicker from '../components/SellPicker';
import useSellCatalog from '../components/useSellCatalog';

const STEPS = [
  { title: 'Business info', description: 'Who the merchant is' },
  { title: 'Domain & database', description: 'Slug, hostname, isolated DB' },
  { title: 'Plan', description: 'Subscription' },
  { title: 'Review & create', description: 'Provision the store' },
];

const EMPTY = {
  brandName: '', legalName: '', gstin: '', city: 'Bengaluru',
  adminName: '', adminEmail: '', contactPhone: '',
  slug: '', customDomain: '', planKey: 'starter',
  // The industries the store is approved to sell in (MERCH-89): it may turn on product types only within them.
  industries: [],
};

const toSlug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 32);

/**
 * Merchant registration & tenant provisioning wizard (mockup 05 §3).
 * Submitting creates the company, its own database `merchantpro_<slug>`, the
 * encrypted connection, runs the provisioning job and returns the first admin.
 */
export default function MerchantWizard() {
  const nav = useNavigate();
  const { fetchApi } = useAppApi();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [plans, setPlans] = useState([]);
  const [avail, setAvail] = useState(null);
  const [checking, setChecking] = useState(false);
  const sellCatalog = useSellCatalog();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchApi('platformPlans', undefined, false).then((r) => setPlans(r.data)).catch((ex) => setErr(errorText(ex)));
  }, []);

  const set = (key) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setForm((f) => {
      const next = { ...f, [key]: value };
      // Suggest a slug from the brand until the admin edits it.
      if (key === 'brandName' && (!f.slug || f.slug === toSlug(f.brandName))) next.slug = toSlug(value);
      return next;
    });
    if (key === 'slug' || key === 'customDomain' || key === 'brandName') setAvail(null);
  };

  const checkAvailability = async () => {
    setChecking(true);
    setErr('');
    try {
      const r = await fetchApi('platformAvailability', { slug: form.slug, domain: form.customDomain }, false);
      setAvail(r.data);
    } catch (ex) {
      setErr(errorText(ex));
    } finally {
      setChecking(false);
    }
  };

  const canNext = [
    form.brandName.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.adminEmail) && form.industries.length > 0,
    avail && avail.slug && avail.slug.available && (!form.customDomain || (avail.domain && avail.domain.available)),
    !!form.planKey,
    true,
  ][step];

  const submit = async () => {
    setSubmitting(true);
    setErr('');
    try {
      const r = await fetchApi('platformCreateMerchant', form, false);
      setResult(r.data);
    } catch (ex) {
      setErr(errorText(ex));
    } finally {
      setSubmitting(false);
    }
  };

  const plan = plans.find((p) => p.key === form.planKey);

  if (result) return <WizardResult result={result} onOpen={() => nav(`/merchants/${result.companyId}`)} />;

  return (
    <div className="space-y-6 max-w-4xl">
      <PageHeader title="Register merchant" subtitle="Creates the company, its own database and the first admin" back={{ label: 'Merchants', href: '/merchants' }} />
      <Card className="p-4">
        <div className="overflow-x-auto">
          <Stepper steps={STEPS} currentStep={step} orientation="horizontal" onStepClick={(_, i) => i < step && setStep(i)} />
        </div>
      </Card>

      {err ? <Alert variant="error" title="Could not continue" dismissible onDismiss={() => setErr('')}>{err}</Alert> : null}

      <Card className="p-4 sm:p-6 space-y-4">
        {step === 0 && (
          <div className="grid sm:grid-cols-2 gap-4">
            <Input label="Store (brand) name" value={form.brandName} onChange={set('brandName')} required placeholder="TechNext Solutions" />
            <Input label="Legal name" value={form.legalName} onChange={set('legalName')} placeholder="TechNext Solutions Pvt Ltd" />
            <Input label="GSTIN" value={form.gstin} onChange={set('gstin')} placeholder="29ABCDE1234F1Z5" maxLength={15} />
            <Input label="City" value={form.city} onChange={set('city')} />
            <Input label="Admin name" value={form.adminName} onChange={set('adminName')} placeholder="Ravi Kumar" />
            <Input label="Admin email" type="email" value={form.adminEmail} onChange={set('adminEmail')} required />
            <Input label="Admin phone" type="tel" value={form.contactPhone} onChange={set('contactPhone')} />
            <div className="sm:col-span-2 space-y-2">
              <div className="text-sm font-semibold">Approved industries</div>
              <p className="text-xs opacity-60">The store can turn on product types only in these industries.</p>
              <SellPicker catalog={sellCatalog} value={{ industries: form.industries, types: [] }} onChange={(v) => setForm((f) => ({ ...f, industries: v.industries }))} />
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                label="Company slug"
                value={form.slug}
                onChange={(e) => set('slug')(toSlug(e.target.value))}
                helperText="Identifies the merchant everywhere: default domain and database name"
                required
              />
              <Input
                label="Custom domain (optional)"
                value={form.customDomain}
                onChange={set('customDomain')}
                placeholder="technext.in"
                helperText="Routes only after DNS verification"
              />
            </div>
            <Button variant="outline" onClick={checkAvailability} loading={checking} disabled={!form.slug}>Check availability</Button>
            {avail && avail.slug ? (
              <div className="space-y-2">
                <Alert variant={avail.slug.available ? 'success' : 'error'} title={avail.slug.available ? 'Slug is available' : 'Slug is taken or invalid'}>
                  <div className="text-sm space-y-1">
                    <div>Store URL: <strong>{avail.slug.defaultHost}</strong></div>
                    <div>Admin portal: <strong>{avail.slug.defaultHost}/admin</strong></div>
                    <div>Database (auto-created, isolated): <strong>{avail.slug.databaseName}</strong></div>
                  </div>
                </Alert>
                {avail.domain ? (
                  <Alert variant={avail.domain.available ? 'success' : 'error'} title={avail.domain.available ? `${avail.domain.value} is available` : `${avail.domain.value} is taken or invalid`} />
                ) : null}
              </div>
            ) : null}
          </div>
        )}

        {step === 2 && (
          <div className="grid sm:grid-cols-3 gap-4">
            {plans.length === 0 ? <Loader text="Loading plans…" /> : null}
            {plans.map((p) => (
              <Card key={p.key} hover className={`p-4 space-y-2 cursor-pointer ${form.planKey === p.key ? 'ring-2 ring-primary-500' : ''}`}>
                <button type="button" className="w-full text-left space-y-2" onClick={() => set('planKey')(p.key)}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{p.name}</span>
                    {form.planKey === p.key ? <Badge variant="primary">Selected</Badge> : null}
                  </div>
                  <div className="text-2xl font-bold">{formatINR(p.price_paise)}<span className="text-sm font-normal opacity-60"> /{p.billing_period}</span></div>
                  <ul className="text-sm space-y-1 opacity-80">
                    {(p.features || []).map((f) => <li key={f}>✓ {f}</li>)}
                  </ul>
                </button>
              </Card>
            ))}
          </div>
        )}

        {step === 3 && (
          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Row k="Store" v={form.brandName} />
            <Row k="Legal name" v={form.legalName || form.brandName} />
            <Row k="Admin" v={`${form.adminName || 'Store Admin'} · ${form.adminEmail}`} />
            <Row k="Plan" v={plan ? `${plan.name} — ${formatINR(plan.price_paise)}/${plan.billing_period}` : form.planKey} />
            <Row k="Default domain" v={avail && avail.slug ? avail.slug.defaultHost : form.slug} />
            <Row k="Custom domain" v={form.customDomain || '—'} />
            <Row k="Database" v={avail && avail.slug ? avail.slug.databaseName : '—'} />
            <Row k="GSTIN" v={form.gstin || '—'} />
            <Row k="Approved industries" v={sellCatalog.filter((i) => form.industries.includes(i.key)).map((i) => i.label).join(', ') || '—'} />
          </dl>
        )}
      </Card>

      <div className="flex justify-between gap-2">
        <Button variant="outline" onClick={() => (step === 0 ? nav('/merchants') : setStep(step - 1))} disabled={submitting}>
          {step === 0 ? 'Cancel' : 'Back'}
        </Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={() => setStep(step + 1)} disabled={!canNext}>Next</Button>
        ) : (
          <Button onClick={submit} loading={submitting}>Create & provision</Button>
        )}
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex flex-col">
      <dt className="opacity-60">{k}</dt>
      <dd className="font-medium break-all">{v}</dd>
    </div>
  );
}

function WizardResult({ result, onOpen }) {
  const ok = result.status === 'active';
  return (
    <div className="space-y-4 max-w-2xl">
      <h1 className="text-xl font-bold">{ok ? 'Merchant is live' : 'Provisioning needs attention'}</h1>
      <Alert variant={ok ? 'success' : 'error'} title={ok ? `${result.slug} provisioned` : 'Provisioning stopped'}>
        {ok ? (
          <div className="text-sm space-y-1">
            <div>Database: <strong>{result.database}</strong></div>
            <div>Domains: <strong>{(result.hosts || []).join(', ')}</strong></div>
          </div>
        ) : (
          <div className="text-sm">{result.error}. The merchant stays offline until a retry succeeds.</div>
        )}
      </Alert>
      {result.admin && result.admin.password ? (
        <Alert variant="warning" title="First admin password — shown once">
          <div className="text-sm space-y-1">
            <div>{result.admin.email}</div>
            <code className="block p-2 rounded bg-[var(--app-surface)] select-all">{result.admin.password}</code>
            <div>Share it securely; it is not stored in plaintext and cannot be shown again.</div>
          </div>
        </Alert>
      ) : null}
      <Button onClick={onOpen}>Open merchant</Button>
    </div>
  );
}
